/* eslint-disable sonarjs/cognitive-complexity*/
import { Unit } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { Row } from "views/applications-id-report/components/Row";
import { useAuthContext } from "providers/Auth";
import { PropertyManagementSystem } from "shared/types";
import { formatToCurrency } from "shared/utils";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import moment from "moment";
import { useState } from "react";
import { RentalInfoForm } from "shared/app/RentalInfoForm";
import { useFormTyped } from "shared/hooks";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import addressApi from "api/landlord/address";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { PMSRentalInfoForm } from "shared/app/RentalInfoForm/PMSRentalInfoForm";
import * as yup from "yup";

const getRentalInfoValidationSchema = ({ isMA }: { isMA: boolean }) => {
  return yup.object().shape({
    monthly_rent_amount: isMA
      ? yup.number().optional()
      : yup
          .number()
          .required("Monthly rent is required")
          .typeError("Monthly rent must be a valid number")
          .min(1, "Monthly rent must be at least $1")
          .max(50000, "Monthly rent must be $50,000 or less"),

    home_rent_amount: isMA
      ? yup
          .number()
          .required("Home rent is required")
          .typeError("Home rent must be a valid number")
          .min(0, "Home rent must be at least $0")
          .max(50000, "Home rent must be $50,000 or less")
      : yup.number().optional(),
    site_rent_amount: isMA
      ? yup
          .number()
          .required("Site rent is required")
          .typeError("Site rent must be a valid number")
          .min(0, "Site rent must be at least $0")
          .max(50000, "Site rent must be $50,000 or less")
      : yup.number().optional(),

    available_at: yup
      .string()
      .nullable()
      .test("is-valid-date", "Please enter a valid date", (value) => {
        // Allow null values
        if (!value) return true;
        const date = moment(value);
        return date.isValid();
      })
  });
};

const EditRentalInfoForm = ({
  onClose,
  unitId,
  onSuccess,
  initialValues,
  pmsId
}: {
  onClose: () => void;
  unitId: number;
  onSuccess: () => void;
  initialValues: {
    monthly_rent_amount?: number;
    home_rent_amount?: number;
    site_rent_amount?: number;
    occupied: boolean;
    available_at?: string;
    pms_monthly_rent_amount_override?: boolean;
    pms_home_rent_amount_override?: boolean;
    pms_site_rent_amount_override?: boolean;
    pms_occupied_override?: boolean;
    pms_available_at_override?: boolean;

    pms_monthly_rent_amount?: number;
    pms_home_rent_amount?: number;
    pms_site_rent_amount?: number;
    pms_occupied?: boolean;
    pms_available_at?: string;
  };
  pmsId?: string;
}) => {
  const { type, permissions, partner } = useAuthContext();
  const isMA =
    partner?.property_management_system?.name ===
    PropertyManagementSystem.ManageAmerica;

  const { register, onSubmit, values, handleChange } = useFormTyped(
    initialValues,
    getRentalInfoValidationSchema({ isMA })
  );

  const buildRentFields = () => {
    if (
      isMA &&
      values.home_rent_amount !== undefined &&
      values.site_rent_amount !== undefined
    ) {
      const homeCents = Math.round(
        parseFloat(String(values.home_rent_amount)) * 100
      );
      const siteCents = Math.round(
        parseFloat(String(values.site_rent_amount)) * 100
      );
      return {
        monthly_rent_amount: {
          cents: homeCents + siteCents,
          currency: "USD"
        },
        home_rent_amount: { cents: homeCents, currency: "USD" },
        site_rent_amount: { cents: siteCents, currency: "USD" }
      };
    }

    return {
      monthly_rent_amount:
        values.monthly_rent_amount && values.monthly_rent_amount > 0
          ? {
              cents: Math.round(values.monthly_rent_amount * 100),
              currency: "USD"
            }
          : undefined
    };
  };

  const { mutate: updateProperty, isPending } = useMutation({
    mutationFn: () => {
      return addressApi.editUnit(unitId, {
        ...buildRentFields(),
        occupied: values.occupied,
        available_at: values.available_at,
        pms_monthly_rent_amount_override:
          values.pms_monthly_rent_amount_override,
        pms_home_rent_amount_override: values.pms_home_rent_amount_override,
        pms_site_rent_amount_override: values.pms_site_rent_amount_override,
        pms_occupied_override: values.pms_occupied_override,
        pms_available_at_override: values.pms_available_at_override
      });
    },
    onSuccess: () => {
      toast.success("Rental info updated");
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update rental info: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => {
      updateProperty();
    },
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  if (type.isPMS && pmsId)
    return (
      <PMSRentalInfoForm
        unitPmsId={pmsId}
        handleChange={handleChange}
        values={values}
        register={register}
        footer={
          <SaveCancelButtons
            onSave={(e) => handleSubmit(e)}
            onCancel={onClose}
            saveLoading={isPending}
          />
        }
      />
    );

  return (
    <RentalInfoForm
      canChangeAvailablebyDate={permissions.canChangeAvailablebyDate}
      canChangeRentAmount={permissions.canChangeRentAmount}
      register={register}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    />
  );
};

interface RentalInfoSectionProps {
  propertyId?: number;
  unit: Unit;
}

export const RentalInfoSection = ({
  unit,
  propertyId
}: RentalInfoSectionProps) => {
  const queryClient = useQueryClient();
  const { partner } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);

  const isMA =
    partner?.property_management_system?.name ===
    PropertyManagementSystem.ManageAmerica;

  if (isEditing)
    return (
      <EditRentalInfoForm
        onSuccess={() => {
          const queryKey = propertyId
            ? ["properties", String(propertyId)]
            : ["unit", String(unit.property.id), String(unit.id)];

          queryClient.invalidateQueries({
            queryKey
          });
        }}
        onClose={() => setIsEditing(false)}
        unitId={unit.id}
        pmsId={unit.pms_id}
        initialValues={{
          monthly_rent_amount:
            unit.monthly_rent_amount && "cents" in unit.monthly_rent_amount
              ? unit.monthly_rent_amount.cents / 100
              : undefined,
          home_rent_amount:
            unit.home_rent_amount && "cents" in unit.home_rent_amount
              ? unit.home_rent_amount.cents / 100
              : undefined,
          site_rent_amount:
            unit.site_rent_amount && "cents" in unit.site_rent_amount
              ? unit.site_rent_amount.cents / 100
              : undefined,
          occupied: unit.occupied,
          available_at: unit.available_at,
          pms_monthly_rent_amount_override:
            unit.pms_monthly_rent_amount_override,
          pms_monthly_rent_amount:
            unit.pms_monthly_rent_amount &&
            "cents" in unit.pms_monthly_rent_amount
              ? unit.pms_monthly_rent_amount.cents / 100
              : undefined,
          pms_home_rent_amount_override: unit.pms_home_rent_amount_override,
          pms_home_rent_amount:
            unit.pms_home_rent_amount && "cents" in unit.pms_home_rent_amount
              ? unit.pms_home_rent_amount.cents / 100
              : undefined,
          pms_site_rent_amount_override: unit.pms_site_rent_amount_override,
          pms_site_rent_amount:
            unit.pms_site_rent_amount && "cents" in unit.pms_site_rent_amount
              ? unit.pms_site_rent_amount.cents / 100
              : undefined,
          pms_occupied_override: unit.pms_occupied_override,
          pms_occupied: unit.pms_occupied,
          pms_available_at_override: unit.pms_available_at_override,
          pms_available_at: unit.pms_available_at
        }}
      />
    );

  return (
    <SectionCard
      title="Rental information"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        {isMA ? (
          <>
            <Row
              title="Home rent"
              value={
                unit.home_rent_amount
                  ? formatToCurrency(unit.home_rent_amount)
                  : ""
              }
            />
            <Row
              title="Site rent"
              value={
                unit.site_rent_amount
                  ? formatToCurrency(unit.site_rent_amount)
                  : ""
              }
            />
          </>
        ) : (
          <Row
            title="Monthly rent"
            value={
              unit.monthly_rent_amount
                ? formatToCurrency(unit.monthly_rent_amount)
                : ""
            }
          />
        )}
        <Row
          title="Current status"
          value={
            <Badge
              variant={
                unit.occupied ? BadgeVariant.PRIMARY : BadgeVariant.SUCCESS
              }
              size={BadgeSize.SM}
            >
              {unit.occupied ? "Occupied" : "Available"}
            </Badge>
          }
        />
        <Row
          title="Available at"
          value={
            unit.available_at
              ? moment(unit.available_at).utc().format("MMM D, YYYY")
              : ""
          }
        />
      </div>
    </SectionCard>
  );
};
